// src/modules/Admin/pages/Goals/index.js
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { supabase } from '../../../../config/supabase';
import { useAuth } from '../../../../contexts/AuthContext';
import {
  FaChartLine,
  FaPlus,
  FaEdit,
  FaTrash,
  FaTimes,
  FaCheck,
  FaSpinner,
  FaExclamationTriangle,
  FaCalendarAlt,
  FaUsers,
  FaDollarSign,
  FaPercent,
  FaArrowUp,
  FaArrowDown,
  FaMinus
} from 'react-icons/fa';

const PERIOD_TYPES = {
  monthly: { label: 'Mensal', color: '#3b82f6' },
  quarterly: { label: 'Trimestral', color: '#8b5cf6' },
  yearly: { label: 'Anual', color: '#10b981' }
};

// Styled Components
const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: #111;
  display: flex;
  align-items: center;
  gap: 12px;

  svg {
    color: #10b981;
  }
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  background: #111;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 200ms;

  &:hover {
    background: #333;
  }
`;

const TabNav = styled.div`
  display: flex;
  gap: 4px;
  margin-bottom: 24px;
  background: #f5f5f5;
  padding: 4px;
  border-radius: 10px;
  width: fit-content;
`;

const Tab = styled.button`
  padding: 10px 20px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 200ms;
  background: ${props => props.$active ? 'white' : 'transparent'};
  color: ${props => props.$active ? '#111' : '#666'};
  box-shadow: ${props => props.$active ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'};

  &:hover {
    color: #111;
  }
`;

const CurrentGoalCard = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  padding: 24px;
  margin-bottom: 24px;
`;

const CurrentGoalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
`;

const CurrentGoalTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #111;
  display: flex;
  align-items: center;
  gap: 8px;

  span {
    font-size: 12px;
    padding: 4px 10px;
    background: ${props => props.$color || '#3b82f6'}15;
    color: ${props => props.$color || '#3b82f6'};
    border-radius: 12px;
  }
`;

const PeriodInfo = styled.div`
  font-size: 13px;
  color: #666;
  display: flex;
  align-items: center;
  gap: 6px;

  svg {
    color: #999;
  }
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`;

const MetricCard = styled.div`
  background: #f9fafb;
  border-radius: 12px;
  padding: 16px;
  border-left: 4px solid ${props => props.$color || '#3b82f6'};

  .label {
    font-size: 12px;
    color: #666;
    margin-bottom: 8px;
    display: flex;
    align-items: center;
    gap: 6px;

    svg {
      color: ${props => props.$color || '#3b82f6'};
    }
  }

  .values {
    display: flex;
    align-items: baseline;
    gap: 8px;

    .current {
      font-size: 24px;
      font-weight: 700;
      color: #111;
    }

    .target {
      font-size: 14px;
      color: #666;
    }
  }

  .progress-bar {
    height: 6px;
    background: #e5e7eb;
    border-radius: 3px;
    margin-top: 12px;
    overflow: hidden;

    .fill {
      height: 100%;
      border-radius: 3px;
      background: ${props => {
        const percent = props.$percent || 0;
        if (percent >= 100) return '#10b981';
        if (percent >= 75) return '#3b82f6';
        if (percent >= 50) return '#f59e0b';
        return '#ef4444';
      }};
      width: ${props => Math.min(props.$percent || 0, 100)}%;
      transition: width 500ms ease;
    }
  }

  .percent {
    font-size: 12px;
    font-weight: 600;
    margin-top: 8px;
    color: ${props => {
      const percent = props.$percent || 0;
      if (percent >= 100) return '#10b981';
      if (percent >= 75) return '#3b82f6';
      if (percent >= 50) return '#f59e0b';
      return '#ef4444';
    }};
    display: flex;
    align-items: center;
    gap: 4px;
  }
`;

const Card = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  text-align: left;
  padding: 16px 20px;
  font-size: 12px;
  font-weight: 600;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid #f0f0f0;
  background: #fafafa;
`;

const Td = styled.td`
  padding: 16px 20px;
  border-bottom: 1px solid #f5f5f5;
  vertical-align: middle;
`;

const PeriodBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  font-size: 12px;
  font-weight: 500;
  border-radius: 12px;
  background: ${props => PERIOD_TYPES[props.$type]?.color || '#666'}15;
  color: ${props => PERIOD_TYPES[props.$type]?.color || '#666'};
`;

const DateRange = styled.div`
  font-size: 13px;
  color: #333;

  .dates {
    color: #666;
    font-size: 12px;
    margin-top: 2px;
  }
`;

const MetricPreview = styled.div`
  display: flex;
  gap: 16px;
  font-size: 13px;

  .item {
    display: flex;
    align-items: center;
    gap: 4px;
    color: #666;

    strong {
      color: #111;
    }
  }
`;

const ActionsCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: #666;
  cursor: pointer;
  transition: all 200ms;

  &:hover {
    background: ${props => props.$danger ? '#fef2f2' : '#f5f5f5'};
    color: ${props => props.$danger ? '#dc2626' : '#111'};
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;

  svg {
    font-size: 48px;
    color: #ddd;
    margin-bottom: 16px;
  }

  h3 {
    font-size: 18px;
    font-weight: 600;
    color: #333;
    margin-bottom: 8px;
  }

  p {
    font-size: 14px;
    color: #666;
  }
`;

const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px;
  color: #666;

  svg {
    animation: spin 1s linear infinite;
    margin-right: 10px;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

// Modal Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const Modal = styled.div`
  background: white;
  border-radius: 16px;
  max-width: 700px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid #f0f0f0;
`;

const ModalTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #111;
`;

const ModalClose = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 8px;
  background: #f5f5f5;
  color: #666;
  cursor: pointer;
  transition: all 200ms;

  &:hover {
    background: #e5e5e5;
    color: #111;
  }
`;

const ModalBody = styled.div`
  padding: 24px;
`;

const FormSection = styled.div`
  margin-bottom: 24px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const FormSectionTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f0f0f0;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: ${props => props.$cols || '1fr 1fr'};
  gap: 16px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const Label = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  font-size: 14px;
  transition: all 200ms;

  &:focus {
    outline: none;
    border-color: #111;
    box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.05);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  font-size: 14px;
  background: white;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #111;
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  font-size: 14px;
  min-height: 80px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: #111;
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid #f0f0f0;
`;

const Button = styled.button`
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 200ms;

  ${props => props.$primary ? `
    background: #111;
    color: white;
    border: none;
    &:hover:not(:disabled) { background: #333; }
  ` : `
    background: white;
    color: #666;
    border: 1px solid #e5e5e5;
    &:hover { background: #f5f5f5; }
  `}

  ${props => props.$danger && `
    background: #ef4444;
    color: white;
    border: none;
    &:hover:not(:disabled) { background: #dc2626; }
  `}

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const WarningBox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  background: #fffbeb;
  border-radius: 8px;
  border: 1px solid #fde68a;
  margin-bottom: 20px;

  svg {
    color: #f59e0b;
    flex-shrink: 0;
    margin-top: 2px;
  }

  .content {
    .title {
      font-size: 14px;
      font-weight: 600;
      color: #92400e;
      margin-bottom: 4px;
    }

    .message {
      font-size: 13px;
      color: #a16207;
    }
  }
`;

const Goals = () => {
  const { user, isAdmin, logActivity } = useAuth();
  const [goals, setGoals] = useState([]);
  const [currentGoal, setCurrentGoal] = useState(null);
  const [currentMetrics, setCurrentMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('monthly');
  const [showModal, setShowModal] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [formData, setFormData] = useState({
    period_type: 'monthly',
    period_start: '',
    period_end: '',
    leads_target: 0,
    qualified_leads_target: 0,
    conversion_rate_target: 0,
    revenue_target: 0,
    deals_target: 0,
    avg_ticket_target: 0,
    new_clients_target: 0,
    retention_rate_target: 0,
    notes: ''
  });

  useEffect(() => {
    fetchGoals();
    fetchCurrentMetrics();
  }, [activeTab]);

  const fetchGoals = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('company_goals')
        .select('*')
        .eq('period_type', activeTab)
        .order('period_start', { ascending: false });

      if (error) throw error;
      setGoals(data || []);

      // Find current period goal
      const today = new Date().toISOString().split('T')[0];
      const current = data?.find(g =>
        g.period_start <= today && g.period_end >= today
      );
      setCurrentGoal(current);
    } catch (error) {
      console.error('Erro ao buscar metas:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrentMetrics = async () => {
    if (!currentGoal) {
      // Get current month metrics as default
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
      const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];

      try {
        const { data, error } = await supabase.rpc('get_period_metrics', {
          p_start_date: startOfMonth,
          p_end_date: endOfMonth
        });

        if (!error && data && data.length > 0) {
          setCurrentMetrics(data[0]);
        }
      } catch (error) {
        console.error('Erro ao buscar métricas:', error);
      }
    }
  };

  useEffect(() => {
    if (currentGoal) {
      fetchMetricsForGoal(currentGoal);
    }
  }, [currentGoal]);

  const fetchMetricsForGoal = async (goal) => {
    try {
      const { data, error } = await supabase.rpc('get_period_metrics', {
        p_start_date: goal.period_start,
        p_end_date: goal.period_end
      });

      if (!error && data && data.length > 0) {
        setCurrentMetrics(data[0]);
      }
    } catch (error) {
      console.error('Erro ao buscar métricas:', error);
    }
  };

  const openCreateModal = () => {
    setEditingGoal(null);

    // Set default dates based on period type
    const now = new Date();
    let startDate, endDate;

    if (activeTab === 'monthly') {
      startDate = new Date(now.getFullYear(), now.getMonth(), 1);
      endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    } else if (activeTab === 'quarterly') {
      const quarter = Math.floor(now.getMonth() / 3);
      startDate = new Date(now.getFullYear(), quarter * 3, 1);
      endDate = new Date(now.getFullYear(), (quarter + 1) * 3, 0);
    } else {
      startDate = new Date(now.getFullYear(), 0, 1);
      endDate = new Date(now.getFullYear(), 11, 31);
    }

    setFormData({
      period_type: activeTab,
      period_start: startDate.toISOString().split('T')[0],
      period_end: endDate.toISOString().split('T')[0],
      leads_target: 0,
      qualified_leads_target: 0,
      conversion_rate_target: 0,
      revenue_target: 0,
      deals_target: 0,
      avg_ticket_target: 0,
      new_clients_target: 0,
      retention_rate_target: 0,
      notes: ''
    });
    setShowModal(true);
  };

  const openEditModal = (goal) => {
    setEditingGoal(goal);
    setFormData({
      period_type: goal.period_type,
      period_start: goal.period_start,
      period_end: goal.period_end,
      leads_target: goal.leads_target || 0,
      qualified_leads_target: goal.qualified_leads_target || 0,
      conversion_rate_target: goal.conversion_rate_target || 0,
      revenue_target: goal.revenue_target || 0,
      deals_target: goal.deals_target || 0,
      avg_ticket_target: goal.avg_ticket_target || 0,
      new_clients_target: goal.new_clients_target || 0,
      retention_rate_target: goal.retention_rate_target || 0,
      notes: goal.notes || ''
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingGoal(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.period_start || !formData.period_end) return;

    setSaving(true);
    try {
      const payload = {
        period_type: formData.period_type,
        period_start: formData.period_start,
        period_end: formData.period_end,
        leads_target: parseInt(formData.leads_target) || 0,
        qualified_leads_target: parseInt(formData.qualified_leads_target) || 0,
        conversion_rate_target: parseFloat(formData.conversion_rate_target) || 0,
        revenue_target: parseFloat(formData.revenue_target) || 0,
        deals_target: parseInt(formData.deals_target) || 0,
        avg_ticket_target: parseFloat(formData.avg_ticket_target) || 0,
        new_clients_target: parseInt(formData.new_clients_target) || 0,
        retention_rate_target: parseFloat(formData.retention_rate_target) || 0,
        notes: formData.notes,
        updated_at: new Date().toISOString()
      };

      if (editingGoal) {
        const { error } = await supabase
          .from('company_goals')
          .update(payload)
          .eq('id', editingGoal.id);

        if (error) throw error;

        await logActivity('update_goal', 'goal', editingGoal.id, `Meta ${PERIOD_TYPES[formData.period_type].label}`);
      } else {
        payload.created_by = user?.email;
        const { data, error } = await supabase
          .from('company_goals')
          .insert(payload)
          .select()
          .single();

        if (error) throw error;

        await logActivity('create_goal', 'goal', data.id, `Meta ${PERIOD_TYPES[formData.period_type].label}`);
      }

      closeModal();
      fetchGoals();
    } catch (error) {
      console.error('Erro ao salvar meta:', error);
      alert('Erro ao salvar meta. Verifique se já não existe uma meta para este período.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!showDeleteConfirm) return;

    setSaving(true);
    try {
      const { error } = await supabase
        .from('company_goals')
        .delete()
        .eq('id', showDeleteConfirm.id);

      if (error) throw error;

      await logActivity('delete_goal', 'goal', showDeleteConfirm.id);

      setGoals(goals.filter(g => g.id !== showDeleteConfirm.id));
      setShowDeleteConfirm(null);
      if (currentGoal?.id === showDeleteConfirm.id) {
        setCurrentGoal(null);
      }
    } catch (error) {
      console.error('Erro ao excluir meta:', error);
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString + 'T00:00:00').toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  const calculatePercent = (current, target) => {
    if (!target || target === 0) return 0;
    return Math.round((current / target) * 100);
  };

  const getPercentIcon = (percent) => {
    if (percent >= 100) return <FaArrowUp />;
    if (percent >= 75) return <FaMinus />;
    return <FaArrowDown />;
  };

  if (!isAdmin) {
    return (
      <Container>
        <EmptyState>
          <FaChartLine />
          <h3>Acesso Restrito</h3>
          <p>Apenas administradores podem acessar esta página.</p>
        </EmptyState>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>
          <FaChartLine />
          Metas da Empresa
        </Title>
        <AddButton onClick={openCreateModal}>
          <FaPlus />
          Nova Meta
        </AddButton>
      </Header>

      <TabNav>
        <Tab $active={activeTab === 'monthly'} onClick={() => setActiveTab('monthly')}>
          Mensal
        </Tab>
        <Tab $active={activeTab === 'quarterly'} onClick={() => setActiveTab('quarterly')}>
          Trimestral
        </Tab>
        <Tab $active={activeTab === 'yearly'} onClick={() => setActiveTab('yearly')}>
          Anual
        </Tab>
      </TabNav>

      {currentGoal && currentMetrics && (
        <CurrentGoalCard>
          <CurrentGoalHeader>
            <CurrentGoalTitle $color={PERIOD_TYPES[currentGoal.period_type]?.color}>
              Meta Atual
              <span>{PERIOD_TYPES[currentGoal.period_type]?.label}</span>
            </CurrentGoalTitle>
            <PeriodInfo>
              <FaCalendarAlt />
              {formatDate(currentGoal.period_start)} - {formatDate(currentGoal.period_end)}
            </PeriodInfo>
          </CurrentGoalHeader>

          <MetricsGrid>
            <MetricCard
              $color="#3b82f6"
              $percent={calculatePercent(currentMetrics.total_leads, currentGoal.leads_target)}
            >
              <div className="label">
                <FaUsers />
                Leads
              </div>
              <div className="values">
                <span className="current">{currentMetrics.total_leads || 0}</span>
                <span className="target">/ {currentGoal.leads_target || 0}</span>
              </div>
              <div className="progress-bar">
                <div className="fill" />
              </div>
              <div className="percent">
                {getPercentIcon(calculatePercent(currentMetrics.total_leads, currentGoal.leads_target))}
                {calculatePercent(currentMetrics.total_leads, currentGoal.leads_target)}%
              </div>
            </MetricCard>

            <MetricCard
              $color="#8b5cf6"
              $percent={calculatePercent(currentMetrics.qualified_leads, currentGoal.qualified_leads_target)}
            >
              <div className="label">
                <FaCheck />
                Qualificados
              </div>
              <div className="values">
                <span className="current">{currentMetrics.qualified_leads || 0}</span>
                <span className="target">/ {currentGoal.qualified_leads_target || 0}</span>
              </div>
              <div className="progress-bar">
                <div className="fill" />
              </div>
              <div className="percent">
                {getPercentIcon(calculatePercent(currentMetrics.qualified_leads, currentGoal.qualified_leads_target))}
                {calculatePercent(currentMetrics.qualified_leads, currentGoal.qualified_leads_target)}%
              </div>
            </MetricCard>

            <MetricCard
              $color="#10b981"
              $percent={calculatePercent(currentMetrics.converted_leads, currentGoal.deals_target)}
            >
              <div className="label">
                <FaDollarSign />
                Conversões
              </div>
              <div className="values">
                <span className="current">{currentMetrics.converted_leads || 0}</span>
                <span className="target">/ {currentGoal.deals_target || 0}</span>
              </div>
              <div className="progress-bar">
                <div className="fill" />
              </div>
              <div className="percent">
                {getPercentIcon(calculatePercent(currentMetrics.converted_leads, currentGoal.deals_target))}
                {calculatePercent(currentMetrics.converted_leads, currentGoal.deals_target)}%
              </div>
            </MetricCard>

            <MetricCard
              $color="#f59e0b"
              $percent={calculatePercent(currentMetrics.conversion_rate, currentGoal.conversion_rate_target)}
            >
              <div className="label">
                <FaPercent />
                Taxa de Conversão
              </div>
              <div className="values">
                <span className="current">{currentMetrics.conversion_rate || 0}%</span>
                <span className="target">/ {currentGoal.conversion_rate_target || 0}%</span>
              </div>
              <div className="progress-bar">
                <div className="fill" />
              </div>
              <div className="percent">
                {getPercentIcon(calculatePercent(currentMetrics.conversion_rate, currentGoal.conversion_rate_target))}
                {calculatePercent(currentMetrics.conversion_rate, currentGoal.conversion_rate_target)}%
              </div>
            </MetricCard>
          </MetricsGrid>
        </CurrentGoalCard>
      )}

      <Card>
        {loading ? (
          <LoadingState>
            <FaSpinner />
            Carregando metas...
          </LoadingState>
        ) : goals.length === 0 ? (
          <EmptyState>
            <FaChartLine />
            <h3>Nenhuma meta cadastrada</h3>
            <p>Crie metas para acompanhar o desempenho da empresa.</p>
          </EmptyState>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Período</Th>
                <Th>Leads</Th>
                <Th>Conversões</Th>
                <Th>Receita</Th>
                <Th style={{ width: '100px' }}>Ações</Th>
              </tr>
            </thead>
            <tbody>
              {goals.map((goal) => (
                <tr key={goal.id}>
                  <Td>
                    <DateRange>
                      <PeriodBadge $type={goal.period_type}>
                        {PERIOD_TYPES[goal.period_type]?.label}
                      </PeriodBadge>
                      <div className="dates">
                        {formatDate(goal.period_start)} - {formatDate(goal.period_end)}
                      </div>
                    </DateRange>
                  </Td>
                  <Td>
                    <MetricPreview>
                      <div className="item">
                        <strong>{goal.leads_target || 0}</strong> leads
                      </div>
                    </MetricPreview>
                  </Td>
                  <Td>
                    <MetricPreview>
                      <div className="item">
                        <strong>{goal.deals_target || 0}</strong> deals
                      </div>
                      <div className="item">
                        <strong>{goal.conversion_rate_target || 0}%</strong>
                      </div>
                    </MetricPreview>
                  </Td>
                  <Td>
                    <MetricPreview>
                      <div className="item">
                        <strong>{formatCurrency(goal.revenue_target)}</strong>
                      </div>
                    </MetricPreview>
                  </Td>
                  <Td>
                    <ActionsCell>
                      <ActionButton onClick={() => openEditModal(goal)} title="Editar">
                        <FaEdit />
                      </ActionButton>
                      <ActionButton
                        $danger
                        onClick={() => setShowDeleteConfirm(goal)}
                        title="Excluir"
                      >
                        <FaTrash />
                      </ActionButton>
                    </ActionsCell>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      {/* Modal de Criar/Editar */}
      {showModal && (
        <ModalOverlay onClick={closeModal}>
          <Modal onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>
                {editingGoal ? 'Editar Meta' : 'Nova Meta'}
              </ModalTitle>
              <ModalClose onClick={closeModal}>
                <FaTimes />
              </ModalClose>
            </ModalHeader>
            <ModalBody>
              <form onSubmit={handleSubmit}>
                <FormSection>
                  <FormSectionTitle>Período</FormSectionTitle>
                  <FormRow $cols="1fr 1fr 1fr">
                    <FormGroup>
                      <Label>Tipo *</Label>
                      <Select
                        value={formData.period_type}
                        onChange={(e) => setFormData({ ...formData, period_type: e.target.value })}
                      >
                        <option value="monthly">Mensal</option>
                        <option value="quarterly">Trimestral</option>
                        <option value="yearly">Anual</option>
                      </Select>
                    </FormGroup>
                    <FormGroup>
                      <Label>Data Início *</Label>
                      <Input
                        type="date"
                        value={formData.period_start}
                        onChange={(e) => setFormData({ ...formData, period_start: e.target.value })}
                        required
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label>Data Fim *</Label>
                      <Input
                        type="date"
                        value={formData.period_end}
                        onChange={(e) => setFormData({ ...formData, period_end: e.target.value })}
                        required
                      />
                    </FormGroup>
                  </FormRow>
                </FormSection>

                <FormSection>
                  <FormSectionTitle>Metas de Leads</FormSectionTitle>
                  <FormRow>
                    <FormGroup>
                      <Label>Total de Leads</Label>
                      <Input
                        type="number"
                        min="0"
                        value={formData.leads_target}
                        onChange={(e) => setFormData({ ...formData, leads_target: e.target.value })}
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label>Leads Qualificados</Label>
                      <Input
                        type="number"
                        min="0"
                        value={formData.qualified_leads_target}
                        onChange={(e) => setFormData({ ...formData, qualified_leads_target: e.target.value })}
                      />
                    </FormGroup>
                  </FormRow>
                </FormSection>

                <FormSection>
                  <FormSectionTitle>Metas de Conversão</FormSectionTitle>
                  <FormRow>
                    <FormGroup>
                      <Label>Negócios Fechados</Label>
                      <Input
                        type="number"
                        min="0"
                        value={formData.deals_target}
                        onChange={(e) => setFormData({ ...formData, deals_target: e.target.value })}
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label>Taxa de Conversão (%)</Label>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        step="0.1"
                        value={formData.conversion_rate_target}
                        onChange={(e) => setFormData({ ...formData, conversion_rate_target: e.target.value })}
                      />
                    </FormGroup>
                  </FormRow>
                </FormSection>

                <FormSection>
                  <FormSectionTitle>Metas Financeiras</FormSectionTitle>
                  <FormRow>
                    <FormGroup>
                      <Label>Receita (R$)</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={formData.revenue_target}
                        onChange={(e) => setFormData({ ...formData, revenue_target: e.target.value })}
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label>Ticket Médio (R$)</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={formData.avg_ticket_target}
                        onChange={(e) => setFormData({ ...formData, avg_ticket_target: e.target.value })}
                      />
                    </FormGroup>
                  </FormRow>
                </FormSection>

                <FormSection>
                  <FormSectionTitle>Observações</FormSectionTitle>
                  <FormGroup>
                    <Textarea
                      placeholder="Notas sobre esta meta..."
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    />
                  </FormGroup>
                </FormSection>

                <ModalActions>
                  <Button type="button" onClick={closeModal}>
                    Cancelar
                  </Button>
                  <Button type="submit" $primary disabled={saving}>
                    {saving ? <FaSpinner /> : <FaCheck />}
                    {editingGoal ? 'Salvar' : 'Criar Meta'}
                  </Button>
                </ModalActions>
              </form>
            </ModalBody>
          </Modal>
        </ModalOverlay>
      )}

      {/* Modal de Confirmação de Exclusão */}
      {showDeleteConfirm && (
        <ModalOverlay onClick={() => setShowDeleteConfirm(null)}>
          <Modal onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Confirmar Exclusão</ModalTitle>
              <ModalClose onClick={() => setShowDeleteConfirm(null)}>
                <FaTimes />
              </ModalClose>
            </ModalHeader>
            <ModalBody>
              <WarningBox>
                <FaExclamationTriangle />
                <div className="content">
                  <div className="title">Atenção!</div>
                  <div className="message">
                    Você está prestes a excluir a meta do período{' '}
                    <strong>{formatDate(showDeleteConfirm.period_start)} - {formatDate(showDeleteConfirm.period_end)}</strong>.
                    Esta ação não pode ser desfeita.
                  </div>
                </div>
              </WarningBox>

              <ModalActions>
                <Button type="button" onClick={() => setShowDeleteConfirm(null)}>
                  Cancelar
                </Button>
                <Button type="button" $danger onClick={handleDelete} disabled={saving}>
                  {saving ? <FaSpinner /> : <FaTrash />}
                  Excluir
                </Button>
              </ModalActions>
            </ModalBody>
          </Modal>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default Goals;
